import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Calendar, Clock, Sparkles, Loader2, ExternalLink } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Image } from "@/components/ui/image";
import Navbar from "@/components/tchatix/Navbar";
import Footer from "@/components/tchatix/Footer";
import CalendlyModal from "@/components/tchatix/CalendlyModal";
import DOMPurify from 'dompurify';
// HTML brut rendu via DOMPurify (sans rehype-raw) — markdown pur via ReactMarkdown.
import { setJsonLd, blogPostingLd, SITE_URL } from "@/components/Seo";
import RelatedArticles from "@/components/tchatix/RelatedArticles";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await base44.entities.BlogArticle.filter({ slug }, "-created_date", 1);
        const found = list && list[0];
        if (cancelled) return;
        if (found) {
          setArticle(found);
        } else {
          setNotFound(true);
        }
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {cancelled = true;};
  }, [slug]);

  // 🔥 SEO dynamique : met à jour les balises meta quand l'article est chargé
  useEffect(() => {
    if (!article) return;

    document.title = `${article.title} — Tchatix`;

    let descEl = document.querySelector('meta[name="description"]');
    if (!descEl) {
      descEl = document.createElement('meta');
      descEl.setAttribute('name', 'description');
      document.head.appendChild(descEl);
    }
    descEl.setAttribute('content', article.excerpt || `Article Tchatix : ${article.title}`);

    if (article.cover_image_url) {
      let ogImg = document.querySelector('meta[property="og:image"]');
      if (!ogImg) {
        ogImg = document.createElement('meta');
        ogImg.setAttribute('property', 'og:image');
        document.head.appendChild(ogImg);
      }
      ogImg.setAttribute('content', article.cover_image_url);
    }

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', `${article.title} — Tchatix`);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute('content', article.excerpt || `Article Tchatix : ${article.title}`);

    // og:url + canonical
    const articleUrl = `${SITE_URL.replace(/\/$/, "")}/article/${slug}`;
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) { ogUrl = document.createElement('meta'); ogUrl.setAttribute('property', 'og:url'); document.head.appendChild(ogUrl); }
    ogUrl.setAttribute('content', articleUrl);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', articleUrl);

    // og:type article
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) { ogType = document.createElement('meta'); ogType.setAttribute('property', 'og:type'); document.head.appendChild(ogType); }
    ogType.setAttribute('content', 'article');

    // JSON-LD BlogPosting
    setJsonLd('blogposting', blogPostingLd(article, `/article/${slug}`));

    return () => {
      document.title = "Tchatix — Agents IA sur mesure pour votre structure";
      const defaultDesc = document.querySelector('meta[name="description"]');
      if (defaultDesc) {
        defaultDesc.setAttribute('content', "Plateforme IA française souveraine dédiée aux agents IA sur mesure et à l'automatisation IA : sécurisée, RGPD, conçue pour les PME et ETI.");
      }
      setJsonLd('blogposting', null);
    };
  }, [article, slug]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {document.body.style.overflow = "";};
  }, [showModal]);

  const bodyContent = article?.body_markdown || "";
  const containsHtml = /<\/?[a-z][\s\S]*>/i.test(bodyContent);

  if (loading) {
    return (
      <div className="relative min-h-screen text-[#001D31]">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-7 h-7 animate-spin text-[#0985D8]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="relative min-h-screen text-[#001D31]">
        <Navbar />
        <div className="max-w-2xl mx-auto text-center py-32 px-6">
          <p className="text-5xl mb-4">🔍</p>
          <h1 className="text-2xl font-semibold mb-3 [font-family:'Playfair_Display',_serif]">Article introuvable</h1>
          <p className="text-[#8896B0] mb-6">Cet article n'existe pas ou n'est plus publié.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5A3C] text-white text-sm font-medium hover:bg-[#001D31] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour au blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen text-[#001D31] overflow-x-hidden">
      <Navbar />

      <article className="pt-28 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate("/blog")}
            className="inline-flex items-center gap-2 text-sm text-[#8896B0] hover:text-[#001D31] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Retour au blog
          </button>

          <div className="flex items-center gap-2 mb-5">
            <span className="text-xs font-medium px-3 py-1 rounded-full border border-[#0985D8]/40 text-[#0985D8] bg-[#ffffff]/[0.2]">
              {article.category}
            </span>
            {article.tag &&
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[#001D31]/70">
                {article.tag}
              </span>
            }
          </div>

          <h1 className="text-3xl md:text-5xl font-semibold leading-tight mb-5 [font-family:'Playfair_Display',_serif]">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 mb-8 text-xs text-[#8896B0]">
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(article.created_date)}</span>
            {article.updated_date && article.updated_date !== article.created_date &&
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Mis à jour le {formatDate(article.updated_date)}</span>
            }
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.read_time}</span>
            <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#FF5A3C]" /> Rédigé par l'Agent SEO Tchatix</span>
          </div>

          {article.excerpt &&
            <p className="text-lg text-[#001D31]/80 leading-relaxed mb-8 font-light">{article.excerpt}</p>
          }

          {article.cover_image_url &&
            <div className="rounded-3xl overflow-hidden mb-10 border border-white/10">
              <Image
                src={article.cover_image_url}
                alt={article.title}
                fittingType="cover"
                className="w-full aspect-[4/3] object-center"
              />
            </div>
          }

          <div className="prose-tchatix text-[#001D31] leading-relaxed text-[17px]">
            {containsHtml ? (
              <div
                className="article-html"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyContent) }}
              />
            ) : (
              <ReactMarkdown
                components={{
                  h1: ({ node, ...p }) => <h2 className="text-2xl md:text-3xl font-semibold text-[#001D31] mt-10 mb-4 [font-family:'Playfair_Display',_serif]" {...p} />,
                  h2: ({ node, ...p }) => <h2 className="text-xl md:text-2xl font-semibold text-[#001D31] mt-10 mb-3 [font-family:'Playfair_Display',_serif]" {...p} />,
                  h3: ({ node, ...p }) => <h3 className="text-lg font-semibold text-[#001D31] mt-6 mb-2" {...p} />,
                  p: ({ node, ...p }) => <p className="mb-5 leading-relaxed" {...p} />,
                  ul: ({ node, ...p }) => <ul className="mb-5 space-y-2 pl-1" {...p} />,
                  li: ({ node, ...p }) => <li className="flex gap-2"><span className="text-[#0985D8] mt-1">›</span><span {...p} /></li>,
                  strong: ({ node, ...p }) => <strong className="text-[#001D31] font-semibold" {...p} />,
                  a: ({ node, ...p }) => <a target="_blank" rel="noopener noreferrer" className="text-[#0985D8] underline hover:text-[#00D2FF]" {...p} />,
                  img: ({ node, alt, src }) =>
                    <figure className="my-8">
                      <div className="rounded-2xl overflow-hidden border border-white/10">
                        <Image src={src} alt={alt} fittingType="cover" className="w-full aspect-[16/9]" />
                      </div>
                      {alt && <figcaption className="text-xs text-[#8896B0] text-center mt-2">{alt}</figcaption>}
                    </figure>,
                  table: ({ node, ...props }) =>
                    <div className="my-8 overflow-x-auto rounded-xl border border-white/10 shadow-sm">
                      <table className="w-full border-collapse" {...props} />
                    </div>,
                  th: ({ node, ...props }) =>
                    <th className="bg-[#001D31] text-white font-semibold px-5 py-3.5 text-left text-sm border-b border-white/10" {...props} />,
                  td: ({ node, ...props }) =>
                    <td className="px-5 py-3.5 border-b border-white/10 text-[#001D31] text-sm leading-relaxed" {...props} />
                }}
              >
                {bodyContent}
              </ReactMarkdown>
            )}
          </div>

          {Array.isArray(article.seo_keywords) && article.seo_keywords.length > 0 &&
            <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-2">
              {article.seo_keywords.map((k) =>
                <span key={k} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[#8896B0]">
                  #{k}
                </span>
              )}
            </div>
          }

          <div className="mt-12 flex items-center gap-3 p-5 rounded-2xl bg-[#FF5A3C]/8 border border-[#FF5A3C]/25">
            <Sparkles className="w-5 h-5 text-[#FF5A3C] shrink-0" />
            <p className="text-xs text-[#001D31]/80">
              Cet article a été rédigé par notre Agent SEO Tchatix, puis relu et validé par notre équipe éditoriale. Les sources citées sont vérifiables dans les liens hypertextes du texte.
            </p>
          </div>

          <RelatedArticles currentSlug={slug} category={article.category} />

          <div className="mt-10 text-center tx-glass-strong rounded-3xl p-8">
            <h3 className="text-xl font-semibold mb-2 [font-family:'Playfair_Display',_serif]">Déployez votre propre Agent SEO</h3>
            <p className="text-sm text-[#8896B0] mb-5 max-w-md mx-auto">
              Le même agent peut rédiger votre stratégie de contenu. Réservez un audit gratuit.
            </p>
            <motion.button
              onClick={() => setShowModal(true)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white text-sm font-semibold cursor-pointer border-0"
              style={{ background: "linear-gradient(120deg, #001D31 0%, #0A3A5C 60%, #001D31 100%)" }}
            >
              Réserver mon audit gratuit
              <ExternalLink className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </article>

      <Footer />

      <CalendlyModal open={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}